import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Mail, Lock, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Inbox() {
  const [token, setToken] = useState(localStorage.getItem("inbox_token") || "");
  const [step, setStep] = useState("request");
  const [code, setCode] = useState("");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const loadMessages = async (tk) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/contact`, { headers: { "X-Inbox-Token": tk } });
      setMessages(r.data);
    } catch {
      localStorage.removeItem("inbox_token");
      setToken("");
      setStep("request");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) loadMessages(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const requestOtp = async () => {
    setBusy(true);
    setError("");
    setCode("");
    try {
      await axios.post(`${API}/inbox/request-otp`);
      setStep("code");
    } catch {
      setError("E-Mail-Versand fehlgeschlagen. Bitte erneut versuchen.");
    } finally {
      setBusy(false);
    }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    if (code.length !== 6) return;
    setBusy(true);
    setError("");
    try {
      const r = await axios.post(`${API}/inbox/verify-otp`, { code });
      localStorage.setItem("inbox_token", r.data.token);
      setToken(r.data.token);
      setCode("");
      loadMessages(r.data.token);
    } catch {
      setError("Falscher oder abgelaufener Code.");
    } finally {
      setBusy(false);
    }
  };

  const lock = () => {
    localStorage.removeItem("inbox_token");
    setToken("");
    setMessages([]);
    setStep("request");
    setCode("");
  };

  if (!token) {
    return (
      <main data-testid="inbox-page" className="min-h-screen bg-ink text-paper px-6 flex items-center justify-center">
        <div className="w-full max-w-sm">
          <Link to="/" data-testid="inbox-back-link" className="font-mono text-xs uppercase tracking-[0.25em] text-dim hover:text-volt inline-flex items-center gap-2 mb-10">
            <ArrowLeft size={14} /> Zurück zur Website
          </Link>
          <div className="border border-line bg-graphite p-8">
            <Lock size={20} className="text-volt mb-4" />
            <h1 className="font-heading font-extrabold tracking-tighter text-3xl">
              INBOX<span className="text-volt">.</span>
            </h1>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-dim mt-2 mb-6">
              Privater Bereich — Login per E-Mail-Code
            </p>

            {step === "request" ? (
              <>
                <p className="font-body text-sm text-dim leading-relaxed mb-6">
                  Ein 6-stelliger Einmal-Code wird an deine hinterlegte E-Mail-Adresse gesendet.
                </p>
                <button
                  onClick={requestOtp}
                  disabled={busy}
                  data-testid="inbox-request-otp"
                  className="w-full bg-volt text-ink font-mono text-xs uppercase tracking-[0.25em] py-3.5 hover:bg-paper disabled:opacity-50 inline-flex items-center justify-center gap-2"
                >
                  <Send size={13} /> {busy ? "Sende..." : "Code per E-Mail senden"}
                </button>
              </>
            ) : (
              <form onSubmit={verifyOtp} className="space-y-4">
                <p className="font-body text-sm text-dim leading-relaxed">
                  Code wurde gesendet — gültig für 10 Minuten. Checke dein Postfach.
                </p>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="······"
                  data-testid="inbox-code-input"
                  className="w-full bg-ink border border-line px-4 h-14 font-mono text-2xl tracking-[0.5em] text-center text-paper focus:outline-none focus:border-volt"
                />
                <button
                  type="submit"
                  disabled={busy || code.length !== 6}
                  data-testid="inbox-code-submit"
                  className="w-full bg-volt text-ink font-mono text-xs uppercase tracking-[0.25em] py-3.5 hover:bg-paper disabled:opacity-50"
                >
                  {busy ? "Prüfe..." : "Entsperren"}
                </button>
                <button
                  type="button"
                  onClick={requestOtp}
                  disabled={busy}
                  data-testid="inbox-resend-otp"
                  className="w-full font-mono text-[10px] uppercase tracking-[0.25em] text-dim hover:text-volt py-1"
                >
                  Neuen Code senden
                </button>
              </form>
            )}

            {error && (
              <p data-testid="inbox-code-error" className="font-mono text-xs text-red-400 mt-4">{error}</p>
            )}
          </div>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="inbox-page" className="min-h-screen bg-ink text-paper px-6 lg:px-10 py-16">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-12">
          <Link to="/" data-testid="inbox-back-link" className="font-mono text-xs uppercase tracking-[0.25em] text-dim hover:text-volt inline-flex items-center gap-2">
            <ArrowLeft size={14} /> Zurück zur Website
          </Link>
          <button
            onClick={lock}
            data-testid="inbox-lock-button"
            className="font-mono text-xs uppercase tracking-[0.25em] text-dim hover:text-volt inline-flex items-center gap-2"
          >
            <Lock size={12} /> Sperren
          </button>
        </div>

        <h1 className="font-heading font-extrabold tracking-tighter text-4xl sm:text-5xl lg:text-6xl">
          INBOX<span className="text-volt">.</span>
        </h1>
        <p className="font-mono text-xs text-dim tracking-[0.2em] mt-3 uppercase">
          {loading ? "Lade..." : `${messages.length} Nachricht${messages.length === 1 ? "" : "en"} von Headhuntern`}
        </p>

        <div className="mt-12 space-y-4">
          {!loading && messages.length === 0 && (
            <div data-testid="inbox-empty-state" className="border border-line p-10 text-center">
              <Mail size={24} className="text-dim mx-auto mb-4" />
              <p className="font-body text-dim">Noch keine Nachrichten. Teile deine Website — sie kommen.</p>
            </div>
          )}
          {messages.map((m) => (
            <article key={m.id} data-testid={`inbox-message-${m.id}`} className="border border-line bg-graphite p-6 md:p-8 hover:border-volt/40 transition-colors">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-2 mb-4">
                <h2 className="font-heading font-bold text-lg text-paper">
                  {m.name}
                  {m.company && <span className="text-dim font-normal"> · {m.company}</span>}
                </h2>
                <span className="font-mono text-[10px] text-dim tracking-widest">
                  {new Date(m.created_at).toLocaleString("de-DE")}
                </span>
              </div>
              <a href={`mailto:${m.email}`} className="font-mono text-xs text-volt hover:text-paper">{m.email}</a>
              <p className="font-body text-sm text-paper/90 mt-4 leading-relaxed whitespace-pre-wrap">{m.message}</p>
            </article>
          ))}
        </div>
      </div>
    </main>
  );
}
